import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { Language, languageNames } from '@/context/languages/languageData';
import { extractLanguageFromPath, getCanonicalPath } from '@/utils/routing/multilingualRouting';

interface GlobalSEOManagerProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  type?: 'website' | 'article' | 'profile';
  author?: string;
}

/**
 * Global SEO manager that handles meta tags, hreflang, and structured data
 * for all pages across all languages
 */
export const GlobalSEOManager: React.FC<GlobalSEOManagerProps> = ({
  title = 'Global Therapy Hub - Connect with Certified Psychologists Worldwide',
  description = 'Find certified psychologists, psychiatrists, and psychotherapists worldwide. Online and in-person sessions available in your language.',
  keywords = 'psychology, therapy, psychologist, psychiatrist, psychotherapist, mental health, online therapy, global therapy',
  image = 'https://psychotherapist.help/og-image.png',
  type = 'website',
  author = 'Dr. José Luis Marín'
}) => {
  const { language, t } = useLanguage();
  const location = useLocation();
  
  const currentUrl = `https://psychotherapist.help${location.pathname}`;
  const canonicalPath = getCanonicalPath(location.pathname);
  const canonicalUrl = `https://psychotherapist.help${canonicalPath}`;
  
  // Generate alternate URLs for hreflang
  const generateAlternateUrls = (): Array<{ href: string; hreflang: string }> => {
    const supportedLanguages: Language[] = ['en', 'es', 'fr', 'de', 'it', 'pt'];
    
    return supportedLanguages.map(lang => ({
      href: lang === 'en' 
        ? `https://psychotherapist.help${canonicalPath}` 
        : `https://psychotherapist.help/${lang}${canonicalPath}`,
      hreflang: lang
    }));
  };

  const alternateUrls = generateAlternateUrls();

  // Localize meta content based on current language
  const localizedTitle = language === 'en' ? title : t(`seo.${title}`) || title;
  const localizedDescription = language === 'en' ? description : t(`seo.${description}`) || description;

  return (
    <Helmet>
      {/* Basic meta tags */}
      <title>{localizedTitle}</title>
      <meta name="description" content={localizedDescription} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      
      {/* Language and region */}
      <html lang={language} />
      <meta name="language" content={language} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Hreflang tags */}
      {alternateUrls.map(({ href, hreflang }) => (
        <link 
          key={hreflang}
          rel="alternate" 
          hrefLang={hreflang} 
          href={href} 
        />
      ))}
      
      {/* x-default for international targeting */}
      <link 
        rel="alternate" 
        hrefLang="x-default" 
        href={`https://psychotherapist.help${canonicalPath}`} 
      />
      
      {/* Open Graph */}
      <meta property="og:title" content={localizedTitle} />
      <meta property="og:description" content={localizedDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Global Therapy Hub" />
      <meta property="og:locale" content={language === 'en' ? 'en_US' : `${language}_${language.toUpperCase()}`} />
      
      {/* Alternate locales for Open Graph */}
      {alternateUrls
        .filter(alt => alt.hreflang !== language)
        .map(({ hreflang }) => (
          <meta 
            key={`og-locale-${hreflang}`}
            property="og:locale:alternate" 
            content={hreflang === 'en' ? 'en_US' : `${hreflang}_${hreflang.toUpperCase()}`} 
          />
        ))
      }
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={localizedTitle} />
      <meta name="twitter:description" content={localizedDescription} />
      <meta name="twitter:image" content={image} />
      
      {/* Additional SEO meta tags */}
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      <meta name="bingbot" content="index, follow" />
      
      {/* Structured data for organization */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Global Therapy Hub",
          "description": localizedDescription,
          "url": "https://psychotherapist.help",
          "logo": "https://psychotherapist.help/logo.png",
          "founder": {
            "@type": "Person",
            "name": "Dr. José Luis Marín",
            "description": "Psychiatrist, Psychotherapist and Trainer with over 40 years of experience"
          },
          "sameAs": [
            "https://formacionpsicoterapia.com"
          ],
          "areaServed": "Worldwide",
          "serviceType": "Mental Health Services",
          "availableLanguage": alternateUrls.map(alt => ({
            "@type": "Language",
            "name": languageNames[alt.hreflang as Language] || alt.hreflang
          }))
        })}
      </script>
      
      {/* Additional language-specific structured data */}
      {language !== 'en' && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "url": currentUrl,
            "inLanguage": language,
            "isPartOf": {
              "@type": "WebSite",
              "url": "https://psychotherapist.help"
            },
            "potentialAction": {
              "@type": "ReadAction",
              "target": currentUrl
            }
          })}
        </script>
      )}
    </Helmet>
  );
};

export default GlobalSEOManager;